import axios from 'axios';
import { toast } from 'sonner';

const TOKEN_KEY = 'admin_token';
const REAUTH_CHANNEL = 'admin-auth';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken() || ''}` },
});

const bc =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(REAUTH_CHANNEL) : null;

export const announceTokenRenewed = (token) => {
  if (!token) return;
  try {
    bc && bc.postMessage({ type: 'token-renewed', token });
  } catch (_) {}
};

let reauthPromise = null;
let reauthResolve = null;
let reauthWindow = null;
let expiredToastId = null;

const deliverNewToken = (newToken) => {
  if (!newToken || !reauthResolve) return;
  const resolve = reauthResolve;
  reauthResolve = null;
  reauthPromise = null;
  if (expiredToastId !== null) {
    toast.dismiss(expiredToastId);
    expiredToastId = null;
  }
  toast.success('Sessão renovada!');
  try {
    reauthWindow && !reauthWindow.closed && reauthWindow.close();
  } catch (_) {}
  reauthWindow = null;
  resolve(newToken);
};

if (bc) {
  bc.onmessage = (evt) => {
    if (evt?.data?.type === 'token-renewed') deliverNewToken(evt.data.token);
  };
}

window.addEventListener('storage', (e) => {
  if (e.key !== TOKEN_KEY) return;
  if (e.newValue && e.newValue !== e.oldValue) deliverNewToken(e.newValue);
});

const openReauthTab = () => {
  const url = `${window.location.origin}/admin/login?reauth=1`;
  try {
    const w = window.open(url, 'admin-reauth');
    if (w) {
      reauthWindow = w;
      try { w.focus(); } catch (_) {}
      return true;
    }
  } catch (_) {}
  return false;
};

const requestReauth = () => {
  if (reauthPromise) {
    try {
      reauthWindow && !reauthWindow.closed && reauthWindow.focus();
    } catch (_) {}
    return reauthPromise;
  }

  reauthPromise = new Promise((resolve) => {
    reauthResolve = resolve;
  });

  const opened = openReauthTab();

  if (expiredToastId !== null) toast.dismiss(expiredToastId);
  expiredToastId = toast.warning(
    opened
      ? 'É necessário se autenticar novamente. Faça login na nova aba — seu progresso aqui será preservado.'
      : 'É necessário se autenticar novamente. Pop-up bloqueado — clique para abrir o login.',
    {
      duration: Infinity,
      action: { label: opened ? 'Reabrir aba' : 'Abrir login', onClick: () => openReauthTab() },
    }
  );

  return reauthPromise;
};

const isAuthLoginUrl = (url = '') => url.includes('/auth/login');
const isAuthChangePasswordUrl = (url = '') => url.includes('/auth/change-password');

const attachReauthInterceptor = (instance) => {
  // Request guard: if a request is going out with a bearer header but no token
  // (e.g. another tab logged out and wiped localStorage), trigger reauth
  // BEFORE the server roundtrip so callers don't see a generic error toast.
  instance.interceptors.request.use(async (config) => {
    const url = config?.url || '';
    if (isAuthLoginUrl(url)) return config;

    const headers = config.headers || {};
    const authHeader = headers.Authorization || headers.authorization || '';
    const wantsAuth = String(authHeader).startsWith('Bearer');
    if (!wantsAuth) return config;

    if (!getToken()) {
      const newToken = await requestReauth();
      config.headers = headers;
      config.headers.Authorization = `Bearer ${newToken}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      const url = original?.url || '';
      if (
        status !== 401 ||
        !original ||
        original._reauthRetried ||
        isAuthLoginUrl(url) ||
        isAuthChangePasswordUrl(url)
      ) {
        return Promise.reject(error);
      }
      original._reauthRetried = true;
      try {
        const newToken = await requestReauth();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return instance.request(original);
      } catch (_) {
        return Promise.reject(error);
      }
    }
  );
};

let installed = false;
export const installAuthInterceptors = () => {
  if (installed) return;
  installed = true;
  attachReauthInterceptor(axios);
};
